import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const STORAGE_KEY = 'orbit.currentBrand';

const BrandContext = createContext(null);
const BrandOriginRefContext = createContext(null);

function readStoredBrand() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
function writeStoredBrand(origin) {
  try {
    if (origin) localStorage.setItem(STORAGE_KEY, origin);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Initial-Brand-Origin für Pre-Auth-Calls. Siehe getDefaultBrandOrigin() im
 * ApiClient — gleiche Priorität: localStorage → runtime-config → VITE-Env →
 * 'valitype.com' Fallback.
 */
function getInitialBrandOrigin() {
  const stored = readStoredBrand();
  if (stored) return stored;
  if (typeof window !== 'undefined' && window.__ORBIT_CONFIG__?.defaultBrandOrigin) {
    return window.__ORBIT_CONFIG__.defaultBrandOrigin;
  }
  if (import.meta.env.VITE_DEFAULT_BRAND_ORIGIN) {
    return import.meta.env.VITE_DEFAULT_BRAND_ORIGIN;
  }
  if (import.meta.env.PROD) return null;
  return 'valitype.com';
}

/**
 * Brand-State:  'idle' | 'loading' | 'ready' | 'no-brands' | 'error'
 * URL-Slug ist source-of-truth (siehe BrandShell-sync), localStorage hält die
 * letzte Auswahl über Tab-Closes.
 */
export function BrandProvider({ children }) {
  const [brands, setBrandsState] = useState([]);
  // Initial-Wert deckt auch Pre-Auth-Login-Calls ab (Core verlangt Brand-Origin
  // auf /v1/auth/*) — siehe getInitialBrandOrigin() Fallback-Kaskade.
  const [currentBrandOrigin, setOriginState] = useState(() => getInitialBrandOrigin());
  const [status, setStatus] = useState('idle');

  const originRef = useRef(currentBrandOrigin);

  const setBrands = useCallback((b) => setBrandsState(b), []);

  // Brand-Origin synchron in ref + storage + state schreiben. Reihenfolge
  // wichtig: ref ZUERST damit inflight onRequest-Hooks im API-Client den
  // neuen Wert sehen, bevor React den Re-Render scheduled. Sonst Race:
  // Mutation post-Switch könnte gegen alte Brand fahren.
  const setCurrentBrandOrigin = useCallback((origin) => {
    originRef.current = origin;
    writeStoredBrand(origin);
    setOriginState(origin);
  }, []);

  const setCurrentBrandSlug = useCallback(
    (slug) => {
      const match = brands.find((b) => b.slug === slug);
      if (!match) return false;
      originRef.current = match.brandOrigin;
      writeStoredBrand(match.brandOrigin);
      setOriginState(match.brandOrigin);
      return true;
    },
    [brands],
  );

  // Cross-Tab-Sync
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== STORAGE_KEY) return;
      originRef.current = e.newValue;
      setOriginState(e.newValue);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const currentBrand = useMemo(
    () => brands.find((b) => b.brandOrigin === currentBrandOrigin) ?? null,
    [brands, currentBrandOrigin],
  );

  const value = useMemo(
    () => ({
      status,
      brands,
      currentBrandOrigin,
      currentBrand,
      setBrands,
      setStatus,
      setCurrentBrandOrigin,
      setCurrentBrandSlug,
    }),
    [
      status,
      brands,
      currentBrandOrigin,
      currentBrand,
      setBrands,
      setCurrentBrandOrigin,
      setCurrentBrandSlug,
    ],
  );

  return (
    <BrandOriginRefContext.Provider value={originRef}>
      <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
    </BrandOriginRefContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used inside <BrandProvider>');
  return ctx;
}

/** Stable Ref für den API-Client. */
export function useBrandRef() {
  const ref = useContext(BrandOriginRefContext);
  if (!ref) throw new Error('useBrandRef must be used inside <BrandProvider>');
  return ref;
}
